import React, { useState, useRef, ChangeEvent } from 'react';
import { FiUpload, FiFilePlus, FiCheckCircle, FiAlertCircle, FiDownload } from 'react-icons/fi';
// @ts-ignore
import * as XLSX from 'xlsx';
import { createProductTemplate } from '../../utils/createTemplateFile';
import { uploadProducts, publishProducts, ApiProduct, uploadProductImages } from '../../api/productApi';

// Extend HTMLInputElement to include non-standard attributes
declare module 'react' {
  interface HTMLAttributes<T> {
    // Add non-standard directory input attributes
    directory?: string;
    webkitdirectory?: string;
  }
}

interface StagedProduct {
  id: string;
  brand: string;
  type: string;
  collab?: string;
  colorway: string;
  size: string;
  sizeType: string;
  condition: string;
  price: number;
  ogBox?: boolean;
  description?: string;
  imageUrls: string[];
  hasImages: boolean;
}

// Helper function to ensure images display correctly
const getImageUrl = (imageUrl?: string): string => {
  if (!imageUrl) {
    return '';
  }
  
  // If it's a blob URL and we're in production, handle appropriately
  if (imageUrl.startsWith('blob:') && window.location.hostname !== 'localhost') {
    console.warn('Blob URL detected in production environment, using placeholder');
    return 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60"><rect width="60" height="60" fill="%23333"/><text x="50%" y="50%" font-family="Arial" font-size="10" fill="white" text-anchor="middle" dominant-baseline="middle">Image</text></svg>';
  }
  
  return imageUrl;
};

const ProductUpload: React.FC = () => {
  const [stagedProducts, setStagedProducts] = useState<StagedProduct[]>([]);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isUploading, setIsUploading] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [selectedProductIndex, setSelectedProductIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Download template function
  const handleDownloadTemplate = () => {
    const templateBlob = createProductTemplate();
    const url = URL.createObjectURL(templateBlob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product_upload_template.xlsx';
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  // Process the Excel/CSV file
  const handleExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    const reader = new FileReader();

    setIsUploading(true);
    setStatusMessage('Reading Excel file...');

    reader.onload = async (event) => {
      try {
        console.log('Excel file loaded, parsing data...');
        // @ts-ignore
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        
        // Convert Excel rows to JSON, making column names case-insensitive
        const rawJsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 'A' }) as Record<string, any>[];
        console.log('Raw Excel data:', rawJsonData);
        
        if (rawJsonData.length <= 1) { // Check if there's only headers or no data
          setStatusMessage('No data found in the spreadsheet. Please check the format.');
          setUploadStatus('error');
          setIsUploading(false);
          return;
        }
        
        // Extract headers from first row and normalize them to lowercase
        const headers = rawJsonData[0] as Record<string, any>;
        const normalizedHeaders: {[key: string]: string} = {};
        
        // Create a mapping from column letters to normalized header names
        Object.keys(headers).forEach(colLetter => {
          const headerValue = String(headers[colLetter] || '').toLowerCase().trim();
          normalizedHeaders[colLetter] = headerValue;
        });
        
        console.log('Normalized headers:', normalizedHeaders);
        
        // Process data rows with normalized headers
        const jsonData = [];
        for (let i = 1; i < rawJsonData.length; i++) {
          const row = rawJsonData[i] as Record<string, any>;
          const normalizedRow: {[key: string]: any} = {};
          
          // Map values using the normalized headers
          Object.keys(row).forEach(colLetter => {
            const headerName = normalizedHeaders[colLetter];
            if (headerName) {
              normalizedRow[headerName] = row[colLetter];
            }
          });
          
          jsonData.push(normalizedRow);
        }
        
        console.log('Normalized data:', jsonData);
        
        if (jsonData.length === 0) {
          setStatusMessage('No data found in the spreadsheet. Please check the format.');
          setUploadStatus('error');
          setIsUploading(false);
          return;
        }
        
        // Check for required columns
        const requiredColumns = ['brand', 'type', 'colorway', 'size', 'condition', 'price'];
        const missingColumns = requiredColumns.filter(col => 
          !Object.values(normalizedHeaders).includes(col)
        );
        
        if (missingColumns.length > 0) {
          const errorMsg = `Missing required columns: ${missingColumns.join(', ')}. Please check the template.`;
          console.error(errorMsg);
          setStatusMessage(errorMsg);
          setUploadStatus('error');
          setIsUploading(false);
          return;
        }
        
        // Check if sizetype column exists (not required but recommended)
        const hasSizeType = Object.values(normalizedHeaders).includes('sizetype');
        if (!hasSizeType) {
          console.warn('SizeType column not found, will default to "M" for all products');
        }
        
        // Prepare products from spreadsheet data
        const products: StagedProduct[] = jsonData.map((row: Record<string, any>, index: number) => {
          // Double-check all required fields exist in each row
          for (const col of requiredColumns) {
            if (row[col] === undefined) {
              throw new Error(`Row missing required value for ${col}. Please ensure all cells are filled.`);
            }
          }
          
          // Debug: Log the row data to see what's available
          console.log(`Row ${index + 1} data:`, row);
          
          // More robust sizeType parsing
          let sizeType = 'M'; // Default value
          if (row.sizetype && row.sizetype.trim()) {
            sizeType = row.sizetype.trim();
          } else if (row.sizeType && row.sizeType.trim()) {
            sizeType = row.sizeType.trim();
          }
          console.log(`Row ${index + 1} sizeType resolved to:`, sizeType);
          
          return {
            id: `temp-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
            brand: row.brand,
            type: row.type,
            collab: row.collab || '',
            colorway: row.colorway,
            size: String(row.size),
            sizeType: sizeType, // Use the resolved sizeType
            condition: row.condition,
            price: parseFloat(String(row.price)),
            ogBox: row.ogbox === 'Yes' || row['og_box'] === 'true' || Boolean(row.ogbox || row['og_box']),
            description: row.description || '',
            imageUrls: [],
            hasImages: false
          };
        });
        
        console.log('Products prepared for staging:', products);
        
        // Add new products to staging
        setStagedProducts(products);
        setStatusMessage(`Successfully staged ${products.length} products for upload. Add images, then publish when ready.`);
        setUploadStatus('success');
        
        // Reset file input after successful staging
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        console.error('Error processing Excel file:', error);
        setStatusMessage(`Error processing Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setUploadStatus('error');
      } finally {
        setIsUploading(false);
      }
    };

    reader.onerror = () => {
      console.error('Error reading Excel file');
      setStatusMessage('Failed to read the Excel file. Please try again.');
      setUploadStatus('error');
      setIsUploading(false);
    };

    reader.readAsArrayBuffer(file);
  };

  // Handle image upload for a specific product
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0 || selectedProductIndex === null) return;
    
    const productIndex = selectedProductIndex;
    const allFiles = Array.from(e.target.files);
    const MAX_IMAGES = 20;
    
    // Check if user tried to upload more than the limit
    if (allFiles.length > MAX_IMAGES) {
      setStatusMessage(`Maximum ${MAX_IMAGES} images allowed per upload. Only the first ${MAX_IMAGES} will be used.`);
      setUploadStatus('error');
    }
    
    // Limit to max 20 images per upload
    const files = allFiles.slice(0, MAX_IMAGES);
    
    try {
      setIsUploading(true);
      setStatusMessage('Uploading images to storage...');
      console.log(`Uploading ${files.length} images for product at index ${productIndex}`);
      
      const newProducts = [...stagedProducts];
      const product = newProducts[productIndex];
      
      console.log('Product ID for upload:', product.id);
      
      // Upload files to storage
      const uploadResult = await uploadProductImages(product.id, files);
      console.log('Uploaded image URLs:', uploadResult.urls);
      
      // Update the product with new image URLs
      newProducts[productIndex] = {
        ...product,
        imageUrls: [...product.imageUrls, ...uploadResult.urls],
        hasImages: true
      };
      
      setStagedProducts(newProducts);
      console.log('Updated staged products with new images');
      
      // Reset file input after upload
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
      
      // Update success message
      setStatusMessage(`Successfully added ${files.length} image${files.length > 1 ? 's' : ''} to ${product.brand} ${product.type}`);
      setUploadStatus('success');
    } catch (error) {
      console.error('Error uploading images:', error);
      setStatusMessage(`Failed to upload images: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setUploadStatus('error');
    } finally {
      setIsUploading(false);
    }
  };

  // Add new function to handle image removal
  const handleRemoveImages = (productIndex: number) => {
    const newProducts = [...stagedProducts];
    const product = newProducts[productIndex];
    
    // Remove all images
    newProducts[productIndex] = {
      ...product,
      imageUrls: [],
      hasImages: false
    };
    
    setStagedProducts(newProducts);
    setStatusMessage(`Removed all images from ${product.brand} ${product.type}`);
    setUploadStatus('success');
  };

  // Publish products to the site
  const handlePublishProducts = async () => {
    // Check if all products have images
    const productsWithoutImages = stagedProducts.filter(product => !product.hasImages);
    
    if (productsWithoutImages.length > 0) {
      setStatusMessage(`Warning: ${productsWithoutImages.length} products don't have images. Please add images before publishing.`);
      return;
    }
    
    try {
      setIsPublishing(true);
      setStatusMessage('Uploading products to the system...');
      console.log('Starting product publishing process...');
      
      // First, upload the products to the server
      // Transform frontend data to match backend API expectations
      const productsForApi = stagedProducts.map(product => {
        // Create the API payload with correct field names
        const apiProduct = {
          brand: product.brand,
          type: product.type,
          collab: product.collab,
          colorway: product.colorway,
          size: product.size,
          size_type: product.sizeType || 'M',  // Ensure never null/undefined
          condition: product.condition,
          price: product.price,
          og_box: !!product.ogBox,      // Backend expects snake_case
          description: product.description || '', // Add description field
          status: 'staged' as const,    // Required field for schema
          images: product.imageUrls,
        };
        return apiProduct;
      });
      
              console.log('Product data prepared for API:', productsForApi);
        
        // Debug: Check each product's size_type specifically
        productsForApi.forEach((product, index) => {
          console.log(`Product ${index + 1} size_type for API:`, {
            original_sizeType: stagedProducts[index]?.sizeType,
            api_size_type: product.size_type,
            full_product: product
          });
        });
      
      const uploadedProducts = await uploadProducts(productsForApi as any); // Type bypass - we know the transformation is correct
      console.log('Products uploaded to database:', uploadedProducts);
      
      // Then, publish them to make them active
      setStatusMessage('Products uploaded. Publishing products...');
      const productIds = uploadedProducts.map(p => p.id);
      console.log('Publishing products with IDs:', productIds);
      
      const publishedProducts = await publishProducts(productIds);
      console.log('Published products:', publishedProducts);
      
      setStatusMessage(`${uploadedProducts.length} products have been successfully published!`);
      
      // Clear staged products after successful publishing
      setStagedProducts([]);
      setShowImageUploader(false);
    } catch (error) {
      console.error('Error publishing products:', error);
      setStatusMessage(`Error publishing products: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className="bg-background p-6 rounded-lg shadow-lg border border-gray-800 animate-fade-in">
      <h2 className="text-2xl font-bold text-primary mb-6 flex items-center">
        <span className="bg-accent w-10 h-10 rounded-md flex items-center justify-center mr-3 shadow-md">
          <FiUpload className="text-white" />
        </span>
        Product Upload
      </h2>
      
      {/* File upload section */}
      <div className="mb-10 p-6 bg-background-light/30 rounded-lg border border-gray-800/50 hover-card">
        <h3 className="text-lg font-medium text-primary mb-3 flex items-center">
          <span className="w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center mr-2 text-sm">1</span>
          Upload Product Spreadsheet
        </h3>
        <p className="text-secondary mb-6 pl-8">
          Upload an Excel (.xlsx) or CSV file with columns for Brand, Type, Collab, Colorway, Size, SizeType, Condition, Price, and OG Box.
        </p>
        
        <div className="flex items-center space-x-4 mb-4 pl-8">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="bg-accent hover:bg-accent/90 text-white py-2 px-4 rounded-md flex items-center shadow-md transition-all duration-200 hover:shadow-lg transform hover:scale-105"
            disabled={isUploading || isPublishing}
          >
            <FiFilePlus className="mr-2" />
            Select Spreadsheet
          </button>
          
          <button
            onClick={handleDownloadTemplate}
            className="bg-background hover:bg-gray-800 text-primary py-2 px-4 rounded-md flex items-center border border-gray-700 transition-all duration-200"
            disabled={isUploading || isPublishing}
          >
            <FiDownload className="mr-2" />
            Download Template
          </button>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleExcelUpload}
            accept=".xlsx,.xls,.csv"
            className="hidden"
          />
        </div>
        
        {uploadStatus === 'success' && (
          <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 rounded-md text-green-400 flex items-center pl-8 animate-fade-in">
            <FiCheckCircle className="mr-2 flex-shrink-0" />
            <span>{statusMessage}</span>
          </div>
        )}
        
        {uploadStatus === 'error' && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-md text-red-400 flex items-center pl-8 animate-fade-in">
            <FiAlertCircle className="mr-2 flex-shrink-0" />
            <span>{statusMessage}</span>
          </div>
        )}
      </div>
      
      {/* Hidden file input - moved outside the product loop */}
      <input
        type="file"
        ref={imageInputRef}
        onChange={(e) => handleImageUpload(e)}
        className="hidden"
        accept="image/*"
        multiple
        max={20}
      />
      
      {/* Image upload section - visible after successful spreadsheet upload */}
      {stagedProducts.length > 0 && (
        <div className="mb-10 p-6 bg-background-light/30 rounded-lg border border-gray-800/50 hover-card animate-slide-up">
          <h3 className="text-lg font-medium text-primary mb-3 flex items-center">
            <span className="w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center mr-2 text-sm">2</span>
            Associate Images with Products
          </h3>
          <p className="text-secondary mb-6 pl-8">
            Add images for each product in your spreadsheet. Images are required before publishing.
          </p>
          
          <div className="overflow-x-auto -mx-6 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
            <div className="inline-block min-w-full align-middle px-6">
              <div className="overflow-hidden border border-gray-800 rounded-lg shadow-lg">
                <table className="min-w-full divide-y divide-gray-800">
                  <thead className="bg-background-light">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Brand</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Collab</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Colorway</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Size</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Size Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider sticky right-0 bg-background-light z-10">Upload Images</th>
                    </tr>
                  </thead>
                  <tbody className="bg-background divide-y divide-gray-800 stagger-list">
                    {stagedProducts.map((product, index) => (
                      <tr key={product.id} className="hover:bg-background-light/30 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{product.brand}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{product.type}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">
                          {product.collab ? product.collab : <span className="text-gray-500">-</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{product.colorway}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{product.size} ({product.sizeType})</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">${typeof product.price === 'number' ? product.price.toFixed(2) : parseFloat(String(product.price)).toFixed(2)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-primary sticky right-0 bg-background z-10">
                          <div className="flex flex-col space-y-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProductIndex(index);
                                imageInputRef.current?.click();
                              }}
                              className="bg-accent hover:bg-accent/90 text-white py-1.5 px-3 rounded-md text-sm flex items-center shadow-sm hover:shadow-md transition-all duration-200"
                              disabled={isPublishing}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                              </svg>
                              {product.hasImages ? 'Add More Images' : 'Upload Images'}
                            </button>
                            
                            {product.hasImages && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveImages(index);
                                }}
                                className="bg-red-600/20 hover:bg-red-600/30 text-red-400 py-1 px-2 rounded text-xs flex items-center justify-center transition-colors duration-200"
                                disabled={isPublishing}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Remove All
                              </button>
                            )}
                          </div>
                          
                          {product.hasImages && (
                            <>
                              <div className="mt-2 text-xs text-gray-400">
                                {product.imageUrls.length} image{product.imageUrls.length !== 1 ? 's' : ''} uploaded
                              </div>
                              <div className="mt-2 flex flex-wrap gap-1 max-w-xs">
                                {product.imageUrls.slice(0, 3).map((url, i) => (
                                  <img 
                                    key={i} 
                                    src={getImageUrl(url)} 
                                    alt={`${product.brand} ${product.type} ${i+1}`}
                                    className="h-10 w-10 object-cover rounded-sm transition-transform hover:scale-110 hover:shadow-md cursor-pointer"
                                    onError={(e) => {
                                      // If image fails to load, replace with placeholder
                                      (e.target as HTMLImageElement).src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60"><rect width="60" height="60" fill="%23333"/><text x="50%" y="50%" font-family="Arial" font-size="10" fill="white" text-anchor="middle" dominant-baseline="middle">Error</text></svg>';
                                    }}
                                  />
                                ))}
                                {product.imageUrls.length > 3 && (
                                  <div className="h-10 w-10 flex items-center justify-center bg-background-light rounded-sm text-secondary text-xs">
                                    +{product.imageUrls.length - 3}
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Publish section - visible when products have been staged */}
      {stagedProducts.length > 0 && (
        <div className="p-6 bg-background-light/30 rounded-lg border border-gray-800/50 hover-card animate-slide-up">
          <h3 className="text-lg font-medium text-primary mb-3 flex items-center">
            <span className="w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center mr-2 text-sm">3</span>
            Publish Products
          </h3>
          <p className="text-secondary mb-6 pl-8">
            Review your products and publish them to the site when ready.
          </p>
          
          <div className="flex items-center space-x-4 pl-8">
            <button
              onClick={handlePublishProducts}
              className="bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-md flex items-center shadow-md transition-all duration-200 hover:shadow-lg"
              disabled={isPublishing}
            >
              {isPublishing ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                  Publishing...
                </>
              ) : (
                <>
                  <FiUpload className="mr-2" />
                  Publish Products
                </>
              )}
            </button>
            
            <div className="flex items-center space-x-2 bg-background p-2 rounded-md">
              <div className="flex flex-col items-center justify-center bg-background-light px-3 py-2 rounded-md">
                <span className="text-2xl font-bold text-primary">{stagedProducts.length}</span>
                <span className="text-xs text-secondary">Products</span>
              </div>
              <div className="flex flex-col items-center justify-center bg-background-light px-3 py-2 rounded-md">
                <span className="text-2xl font-bold text-green-500">{stagedProducts.filter(p => p.hasImages).length}</span>
                <span className="text-xs text-secondary">With Images</span>
              </div>
              <div className="flex flex-col items-center justify-center bg-background-light px-3 py-2 rounded-md">
                <span className="text-2xl font-bold text-red-500">{stagedProducts.length - stagedProducts.filter(p => p.hasImages).length}</span>
                <span className="text-xs text-secondary">Missing Images</span>
              </div>
            </div>
          </div>
          
          {statusMessage && (
            <div className="mt-6 p-4 bg-background/50 border border-gray-700 rounded-md text-secondary pl-8 animate-fade-in">
              {statusMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductUpload; 